import React from "react";
import {
  MessageCircle, X, ShieldAlert, Lock, Paperclip, Send, RefreshCw, ArrowUpRight,
} from "lucide-react";
import * as api from "../../api";

export function ChatDrawer({ order, currentUserRole, onClose, onMessagesChange, showToast }) {
  const isAdmin = currentUserRole === "admin";
  const orderStatus = String(order?.status || "").trim().toLowerCase();
  const chatClosed = orderStatus === "completed" || orderStatus === "completado";
  const [messages, setMessages] = React.useState([]);
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [pendingAttachment, setPendingAttachment] = React.useState(null); // {file, previewUrl}
  const [uploadingAttachment, setUploadingAttachment] = React.useState(false);
  const [lightboxUrl, setLightboxUrl] = React.useState(null);
  const listRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const lastCountRef = React.useRef(0);
  const orderIdRef = React.useRef(order.id);
  orderIdRef.current = order.id;

  const load = React.useCallback(async () => {
    try {
      const list = await api.listOrderMessages(orderIdRef.current);
      const grew = list.length > lastCountRef.current;
      lastCountRef.current = list.length;
      setMessages(list);
      setLoading(false);
      if (grew) {
        onMessagesChange?.();
        setTimeout(() => {
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        }, 50);
      }
    } catch (err) {
      setLoading(false);
      showToast?.("Error", err.message || "No se pudieron cargar los mensajes", "error");
    }
  }, [onMessagesChange, showToast]);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const handlePickAttachment = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file later
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      showToast?.("Archivo no válido", "Solo se aceptan imágenes (PNG, JPG, WEBP, GIF).", "error");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      showToast?.("Imagen muy grande", "Máximo 10 MB.", "error");
      return;
    }
    setPendingAttachment({ file: f, previewUrl: URL.createObjectURL(f) });
  };

  const clearPendingAttachment = () => {
    if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    setPendingAttachment(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (chatClosed) return;
    const body = text.trim();
    if (!body && !pendingAttachment) return;
    setSending(true);
    try {
      let attachmentUrl = null;
      if (pendingAttachment) {
        setUploadingAttachment(true);
        const up = await api.uploadSupportScreenshot(pendingAttachment.file);
        attachmentUrl = up?.url || null;
        setUploadingAttachment(false);
      }
      const created = await api.sendOrderMessage(order.id, body, attachmentUrl);
      setText("");
      clearPendingAttachment();
      setMessages((prev) => [...prev, created]);
      lastCountRef.current += 1;
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
      onMessagesChange?.();
    } catch (err) {
      showToast?.("Error", err.message || "No se pudo enviar", "error");
    } finally {
      setSending(false);
      setUploadingAttachment(false);
    }
  };

  const otherSideLabel = isAdmin ? "Cliente" : "Administrador AG7";
  const canSend = (!!text.trim() || !!pendingAttachment) && !sending;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
        data-testid="chat-drawer-backdrop"
      />
      <aside
        data-testid="chat-drawer"
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#0f1015] border-l border-zinc-800 z-50 shadow-2xl flex flex-col animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-purple-950/40 to-cyan-950/40">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-mono uppercase tracking-widest text-purple-300">Chat de Soporte Directo</p>
                <p className="text-sm font-bold text-white truncate">
                  {otherSideLabel} · <span className="font-mono text-cyan-300">{order.id}</span>
                </p>
              </div>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">
              {order.gameName} · {order.packageName}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="chat-drawer-close"
            className="text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 p-2 rounded-full flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 2FA / access banner */}
        <div
          data-testid="chat-2fa-banner"
          className="mx-3 mt-3 p-3 rounded-xl bg-amber-950/40 border border-amber-500/40 text-amber-100 text-[11px] leading-snug flex items-start gap-2"
        >
          <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p>
            <strong className="text-amber-300">Importante:</strong> Si se requiere código de verificación de
            2&nbsp;pasos, PIN de acceso o cualquier dato adicional para completar tu recarga,
            envíalo por este chat. <span className="text-amber-200/70">Puedes adjuntar una captura con el icono del clip. Nunca compartas estos códigos fuera de aquí.</span>
          </p>
        </div>

        {/* Messages */}
        <div
          ref={listRef}
          data-testid="chat-drawer-messages"
          className="flex-1 overflow-y-auto px-3 py-4 space-y-3"
        >
          {loading ? (
            <p className="text-center text-xs text-zinc-500 py-6">Cargando mensajes…</p>
          ) : messages.length === 0 ? (
            <div className="text-center py-10">
              <MessageCircle className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
              <p className="text-xs text-zinc-500">
                Sin mensajes aún. {isAdmin ? "Escribe al cliente sobre este pedido." : "Escríbenos si necesitas soporte para este pedido."}
              </p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = (isAdmin && m.sender_type === "admin") || (!isAdmin && m.sender_type === "client");
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.id}`}
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2 shadow-md ${
                      mine
                        ? "bg-gradient-to-br from-purple-600 to-cyan-600 text-white rounded-br-sm"
                        : "bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-bl-sm"
                    }`}
                  >
                    <p className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-0.5">
                      {m.sender_type === "admin" ? "Admin" : "Cliente"}
                    </p>
                    {m.attachment_url && (
                      <button
                        type="button"
                        onClick={() => setLightboxUrl(m.attachment_url)}
                        data-testid={`chat-message-attachment-${m.id}`}
                        className="block mb-1 rounded-xl overflow-hidden border border-white/15 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        aria-label="Ver imagen adjunta"
                      >
                        <img
                          src={m.attachment_url}
                          alt="Adjunto"
                          className="max-w-[240px] max-h-[240px] object-cover"
                          loading="lazy"
                        />
                      </button>
                    )}
                    {m.message && <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>}
                    <p className="text-[9px] font-mono opacity-60 mt-1 text-right">
                      {new Date(m.created_at).toLocaleString("es-VE", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pending attachment preview */}
        {pendingAttachment && (
          <div className="mx-3 mb-2 p-2 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center gap-2" data-testid="chat-pending-attachment">
            <img
              src={pendingAttachment.previewUrl}
              alt="Vista previa"
              className="w-12 h-12 rounded-md object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-white font-medium truncate">{pendingAttachment.file.name}</p>
              <p className="text-[10px] font-mono text-zinc-500">
                {(pendingAttachment.file.size / 1024).toFixed(1)} KB · Imagen adjunta
              </p>
            </div>
            <button
              onClick={clearPendingAttachment}
              disabled={sending}
              data-testid="chat-remove-attachment"
              className="text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 p-1.5 rounded-full disabled:opacity-40"
              aria-label="Quitar adjunto"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Composer OR closed banner */}
        {chatClosed ? (
          <div
            data-testid="chat-drawer-closed-banner"
            className="p-4 border-t border-zinc-800 bg-gradient-to-r from-emerald-950/40 to-zinc-900 flex items-start gap-3"
          >
            <div className="w-9 h-9 rounded-full bg-emerald-600/30 border border-emerald-500 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="text-xs leading-snug">
              <p className="text-emerald-300 font-bold uppercase tracking-widest text-[10px] font-mono">
                Chat cerrado
              </p>
              <p className="text-zinc-300 mt-0.5">
                Este pedido ya fue completado. Ya no se pueden enviar mensajes. Puedes seguir viendo el historial arriba.
              </p>
            </div>
          </div>
        ) : (
          <form data-testid="chat-drawer-form-1" onSubmit={handleSend} className="p-3 border-t border-zinc-800 bg-[#0b0c10] flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handlePickAttachment}
              data-testid="chat-drawer-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              data-testid="chat-drawer-attach-btn"
              className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-purple-300 p-3 rounded-xl border border-zinc-700 flex-shrink-0"
              aria-label="Adjuntar imagen"
              title="Adjuntar imagen"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              data-testid="chat-drawer-input"
              rows={2}
              maxLength={2000}
              placeholder={pendingAttachment ? "Agrega un mensaje (opcional)…" : "Escribe un mensaje… (Enter para enviar)"}
              className="flex-1 resize-none bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
            />
            <button
              type="submit"
              disabled={!canSend}
              data-testid="chat-drawer-send"
              className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:opacity-95 disabled:opacity-40 text-white font-bold p-3 rounded-xl flex items-center gap-1 shadow-lg"
            >
              {sending
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Send className="w-4 h-4" />}
            </button>
          </form>
        )}
        {uploadingAttachment && (
          <p className="text-[10px] text-cyan-300 font-mono px-3 pb-2">Subiendo imagen…</p>
        )}
      </aside>

      {/* Lightbox for full-size image (mobile-optimized: pinch-zoom, safe viewport) */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center animate-fade-in overflow-auto"
          onClick={() => setLightboxUrl(null)}
          data-testid="chat-lightbox"
          style={{ touchAction: "pinch-zoom" }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            data-testid="chat-lightbox-close"
            className="fixed top-3 right-3 sm:top-4 sm:right-4 z-[61] text-white bg-white/15 hover:bg-white/25 active:bg-white/35 w-11 h-11 rounded-full flex items-center justify-center border border-white/20 shadow-lg"
            aria-label="Cerrar imagen"
          >
            <X className="w-5 h-5" />
          </button>
          <a
            href={lightboxUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid="chat-lightbox-open-new-tab"
            className="fixed top-3 left-3 sm:top-4 sm:left-4 z-[61] text-white bg-white/15 hover:bg-white/25 active:bg-white/35 px-3 h-11 rounded-full flex items-center gap-1.5 border border-white/20 shadow-lg text-xs font-bold"
          >
            <ArrowUpRight className="w-4 h-4" />
            <span>Abrir</span>
          </a>
          <img
            src={lightboxUrl}
            alt="Adjunto ampliado"
            data-testid="chat-lightbox-image"
            className="w-auto h-auto max-w-[100vw] max-h-[100vh] sm:max-w-[95vw] sm:max-h-[92vh] object-contain select-none"
            style={{ touchAction: "pinch-zoom" }}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
        </div>
      )}
    </>
  );
}
